<template>

  <v-container fluid class="page page-claim-org-account">

    <v-card flat class="pt-4 registration-form-area">
      <!-------------------------------->
      <!-- Account claim data loading -->
      <!-------------------------------->
      <template v-if="isDataLoading">
        <v-container class="d-flex flex-column align-center justify-center">
          <v-progress-circular indeterminate :size="50" style="margin-top:30px;" color="primary" />
        </v-container>
      </template>

      <!-------------------------------------->
      <!-- Account claim data loading error -->
      <!-------------------------------------->
      <template v-else-if="dataLoadingError">
        <v-icon class="mr-2" color="#cc0000">mdi-alert-circle-outline</v-icon>
        <span class="error-message-text">{{ dataLoadingError }}</span>
      </template>

      <!----------------------------->
      <!-- Already claimed message -->
      <!----------------------------->
      <template v-else-if="isAlreadyClaimed">
        <div class="confirmation-message-area">
          <div class="main-text">The account has already been claimed.</div>
        </div>
      </template>

      <!------------------------------------>
      <!-- Account claim canceled message -->
      <!------------------------------------>
      <template v-else-if="isCanceled">
        <div class="confirmation-message-area">
          <div class="main-text">Registraton is no longer available.</div>
        </div>
      </template>

      <!---------------------------------->
      <!-- Claimed account confirmation -->
      <!---------------------------------->
      <template v-else-if="registeredUser">
        <div class="confirmation-message-area">
          <div class="main-text">You have claimed the account for {{ orgName }}!</div>
          <div class="mt-5 confirmation-message-text">
            An email has been sent to: <strong style="color:#ffffff">{{ registeredEmail }}</strong>
          </div>
          <v-row align="center" class="mt-8 highlighted-info-container">
            <v-col cols="12" class="d-flex align-center justify-center">
              <v-icon style="font-size:1.6rem;" color="yellow darken-3" class="pr-3">mdi-information-outline</v-icon>
              <div class="highlighted-info-text">You must confirm your email address to login.</div>
            </v-col>
          </v-row>
        </div>
      </template>

      <!------------------------>
      <!-- Claim account form -->
      <!------------------------>
      <v-form class="registration-form" autocomplete="off" v-else>
        <!-- title -->
        <v-row>
          <v-col align="center" class="form-title">
            {{ formTitleText }}
          </v-col>
        </v-row>

        <!-- information area -->
        <v-row class="mt-0">
          <v-col align="center">
            <div class="main-text">You have been invited to join {{ appName }} by {{ inviterName }}</div>
            <!-- <div class="mt-1 email-text">({{ ownerEmail }})</div> -->
          </v-col>
        </v-row>
        <v-row class="mt-6">
          <v-col align="center" class="account-info-container">
            <div class="sub-text">You will be the owner of this account, allowing you to:</div>
            <div class="mt-2 ml-8 sub-text" style="text-align:left;">
              <ul>
                <li>View your {{ appName }} orders</li>
                <li>Manage your org and its locations.</li>
                <li>Invite other members to your org.</li>
              </ul>
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-8">
          <v-col align="center" class="info-text">
            <div>Please provide your name and create a password.</div>
          </v-col>
        </v-row>

        <!-- first name -->
        <v-row class="mt-4" align="center">
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <span>First name:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="7" md="7" lg="7">
            <v-text-field
                outlined
                hide-details
                required
                autocomplete="first-name"
                v-model.trim="firstName" />
          </v-col>
        </v-row>

        <!-- last name -->
        <v-row class="mt-4" align="center">
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <span>Last name:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="7" md="7" lg="7">
            <v-text-field
                outlined
                hide-details
                required
                autocomplete="last-name"
                v-model.trim="lastName" />
          </v-col>
        </v-row>

        <!-- password -->
        <v-row class="mt-4" align="start">
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <div class="pt-4">Password:</div>
          </v-col>
          <v-col cols="12" xs="12" sm="7" md="7" lg="7">
            <v-row>
              <v-col class="mb-0 pb-0">
                <v-text-field
                    outlined
                    required
                    v-model.trim="password"
                    autocomplete="new-password"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    @click:append="showPassword = !showPassword" />
              </v-col>
            </v-row>
            <!-- password requirements -->
            <v-row class="pa-0 ma-0 mb-10">
              <v-col class="pa-0 ma-0">
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="password-requirements-text">
                      Password requirements
                      <v-icon style="font-size:17px;" color="#34b3db" class="pl-1">mdi-information-outline</v-icon>
                    </div>
                  </template>
                  <div>
                    <span>Must be at least 8 characters, and contain:</span><br />
                    <ul class="mt-1">
                      <li>1 number</li>
                      <li>1 uppercase letter</li>
                      <li>1 lowercase letter</li>
                      <li>1 special character</li>
                    </ul>
                  </div>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Accept terms of use -->
        <v-row class="mt-1 mb-4" align="center">
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-checkbox
                label="I agree to the terms"
                v-model="acceptTerms" />
            <!-- <router-link
                class="ml-6"
                :to="{ name: 'terms-of-use' }"
                target="_blank"
                rel="noopener">
              <span>Terms of Use</span>
            </router-link>
            <v-icon class="ml-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon> -->
            <div class="ml-5"><a @click.prevent="isTermsDialogOpen = true" href="#">Terms of Use</a></div>
          </v-col>
        </v-row>

        <!-- Registration error display -->
        <template v-if="processingError">
          <transition name="fade" appear>
            <v-row class="mt-0 mb-1">
              <v-col class="d-flex align-center justify-center">
                <v-icon class="mr-2" color="#cc0000">mdi-alert-circle-outline</v-icon>
                <span class="error-message-text">{{ processingError }}</span>
                <v-btn class="ml-3" @click="clearError">OK</v-btn>
              </v-col>
            </v-row>
          </transition>
        </template>

        <!-- Claim account button -->
        <v-row class="mt-0">
          <v-col class="d-flex align-center justify-center">
            <v-progress-circular indeterminate :size="40" color="primary" style="margin-top:12px;" v-if="isProcessing" />
            <v-btn
                v-else
                xlarge
                :disabled="!canClaim"
                color="primary"
                height="54"
                @click="claimAccount">
              Claim Account
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!------------------------->
    <!-- Terms of Use dialog -->
    <!------------------------->
    <v-dialog persistent fullscreen v-model="isTermsDialogOpen" align="center">
      <v-card align="center" style="background-color:#080404;">
        <route-leaf-emblem style="margin-top:32px;margin-right:10px;" />
        <route-leaf-logo :linkTo="null" style="padding-bottom:14px;" />
        <terms-of-use-page :showBackToApp="false" />
        <v-card-actions class="mt-5">
          <v-btn
              color="primary"
              class="px-5"
              @click="isTermsDialogOpen = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
import RouteLeafEmblem from '@/components/app/RouteLeafEmblem'
import RouteLeafLogo from '@/components/app/RouteLeafLogo'
import TermsOfUsePage from '@/pages/public/TermsOfUsePage'

export default {
  name: 'ClaimOrgAccountPage',

  components: {
    RouteLeafEmblem,
    RouteLeafLogo,
    TermsOfUsePage
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      accountClaim: null, // an OrgRegistrationCode item
      isProcessing: false,
      processingError: null,
      showPassword: false,
      registeredUser: null,
      // --------------- Input fields
      firstName: null,
      lastName: null,
      password: null,
      acceptTerms: false,
      // --------------- Terms dialog
      isTermsDialogOpen: false
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    registrationCode () {
      return this.$route.query?.registration_code
    },
    formTitleText () {
      return `Claim Account for ${this.orgName}`
    },
    isAlreadyClaimed () {
      return (this.accountClaim)
        ? this.accountClaim.status === 'claimed'
        : false
    },
    isCanceled () {
      return (this.accountClaim)
        ? this.accountClaim.status === 'canceled'
        : false
    },
    canClaim () {
      return (
        this.registrationCode &&
        this.firstName &&
        this.lastName &&
        this.password &&
        this.acceptTerms
      )
    },
    orgName () {
      return (this.accountClaim)
        ? this.accountClaim?.org?.name
        : this.appName
    },
    ownerEmail () {
      return this.accountClaim.email
    },
    inviterName () {
      return (this.accountClaim && this.accountClaim.creator)
        ? `${this.accountClaim.creator.first_name} ${this.accountClaim.creator.last_name}`
        : ''
    },
    registeredEmail () {
      return this.registeredUser.email
    }
  },

  async beforeMount () {
    await this.loadAccountClaimData()
  },

  beforeDestroy () {
    this.resetForm()
    this.accountClaim = null
  },

  methods: {
    // Reset form
    resetForm () {
      this.dataLoadingError = null
      this.processingError = null
      this.registeredUser = null
      this.firstName = null
      this.lastName = null
      this.password = null
      this.acceptTerms = false
    },

    // Clear active error
    clearError () {
      this.processingError = null
    },

    // Load account claim data (the OrgRegistrationCode)
    async loadAccountClaimData () {
      if (!this.registrationCode) {
        this.dataLoadingError = 'This is a restricted page.'
        return false
      }

      this.isDataLoading = true

      try {
        const uri = 'org/account-claim'
        const params = {
          registration_code: this.registrationCode
        }
        const orgRegistrationCodePayload = await this.$root.dataApi.get(uri, { params })
        this.accountClaim = this.$root.toModel(orgRegistrationCodePayload)
      } catch (err) {
        if (err.status === 404) this.dataLoadingError = 'The claim is not valid.'
        else this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    },

    // Perform claim account
    async claimAccount () {
      if (!this.canClaim) {
        this.processingError = 'You must provide all fields to proceed.'
        return false
      }

      this.isProcessing = true

      try {
        const uri = 'org/claim-account-new-user'
        const bodyData = {
          registration_code: this.registrationCode,
          first_name: this.firstName,
          last_name: this.lastName,
          password: this.password
        }
        const registeredUserPayload = await this.$root.dataApi.post(uri, { bodyData })
        this.registeredUser = this.$root.toModel(registeredUserPayload)
      } catch (err) {
        // Handle error for user display
        let message = null
        if (err.code) {
          switch (err.code) {
            case 'InvalidPasswordException': message = 'Invalid password provided'; break
            default: message = 'There was an issue claiming the account'
          }
        } else {
          message = err.message || err.text || err
        }
        this.processingError = message
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style scoped>
  .page-claim-org-account {
    margin-top: 60px;
    display: flex;
    justify-content: center;
  }

  .registration-form-area {
    padding: 24px 24px;
    background-color: #080404 !important;
    width: 500px;
    margin: 0 auto;
  }

  .form-title {
    font-size: 1.5rem;
    color: #F8A824;
    font-weight: 400;
  }

  .main-text {
    font-size: 1.1rem;
    color: #9c9c9c;
    font-weight: 400;
  }

  .email-text {
    font-size: 1.1rem;
    color: #9c9c9c;
    font-weight: 400;
  }

  .sub-text {
    font-size: 1.0rem;
    color: #9c9c9c;
    font-weight: 400;
  }

  .info-text {
    font-size: 1.08rem;
    font-weight: 400;
  }

  .account-info-container {
    text-align: center;
    border: 1px solid #3c3c3c;
    border-radius:8px;
  }

  .password-requirements-text {
    cursor: pointer;
    font-size: 1.0rem;
    color: #34b3db;
  }

  .error-message-text {
    font-size: 1.08rem;
    color: #cc0000;
    font-weight: 400;
  }

  .confirmation-message-area {
    text-align: center;
    max-width: 570px;
  }

  .confirmation-message-text {
    font-size: 1.08rem;
    color: #9c9c9c;
    font-weight: 400;
  }

  .highlighted-info-container {
    border: 1px solid #F9A825;
    border-radius: 6px;
  }

  .highlighted-info-text {
    font-size: 1.08rem;
    color: #F9A825;
    font-weight: 400;
  }

  .page-terms-of-use {
    margin-top: 10px;
  }
</style>
